"use client"

import { HttpTypes } from "@medusajs/types"
import { Text, Button } from "@medusajs/ui"
import ShoppingBag from "@/modules/common/icons/shopping-bag"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { addToCartEventBus } from "@/lib/data/cart-event-bus"
import { getProductPrice } from "@/lib/util/get-product-price"
import { useState, useEffect } from "react"

type ProductCardForRelatedProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  countryCode: string
}

export default function ProductCardForRelated({
  product,
  region,
  countryCode,
}: ProductCardForRelatedProps) {
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  const { cheapestPrice } = getProductPrice({ product })
  const thumb = product.thumbnail ?? product.images?.[0]
  const thumbnail = typeof thumb === "string" ? thumb : (thumb as { url?: string })?.url

  // Fallback: external images (e.g. hendi.xcdn.nl) may not fire onLoad reliably
  useEffect(() => {
    if (!thumbnail || imageError) return
    const fallback = setTimeout(() => setImageLoaded(true), 800)
    return () => clearTimeout(fallback)
  }, [thumbnail, imageError])

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!product.variants?.length || !product.variants[0]?.id) return
    setIsAddingToCart(true)
    try {
      addToCartEventBus.emitCartAdd({
        lineItems: [{
          productVariant: {
            ...product.variants[0],
            product: {
              id: product.id,
              title: product.title,
              handle: product.handle,
              thumbnail: product.thumbnail,
              description: product.description,
            },
          },
          quantity: 1,
        }],
        regionId: region.id,
      })
    } catch (err) {
      console.error("Failed to add to cart:", err)
    } finally {
      setIsAddingToCart(false)
    }
  }

  return (
    <LocalizedClientLink href={`/products/${product.handle}`} className="block h-full">
      <div className="group relative h-full overflow-hidden rounded-lg bg-white shadow-sm border border-neutral-200 hover:shadow-lg transition-all duration-200 flex flex-col">
        {/* Image - aspect-[5/6] same as SearchProductCard */}
        <div className="relative aspect-[5/6] overflow-hidden bg-neutral-50 flex-shrink-0">
          {thumbnail && !imageError ? (
            <>
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                  <div className="animate-pulse w-6 h-6 border-2 border-neutral-300 border-t-neutral-600 rounded-full" />
                </div>
              )}
              <img
                src={thumbnail}
                alt={product.title}
                className={`w-full h-full object-contain object-center transition-transform duration-300 group-hover:scale-105 ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                }`}
                loading="lazy"
                onLoad={() => { setImageLoaded(true); setImageError(false) }}
                onError={() => { setImageError(true); setImageLoaded(false) }}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-neutral-100">
              <Text className="text-neutral-400 text-2xl">📦</Text>
            </div>
          )}
        </div>

        {/* Bottom strip - same as SearchProductCard */}
        <div className="flex-shrink-0 p-3 bg-white border-t border-neutral-100 space-y-2">
          <Text className="font-semibold text-sm text-neutral-800 leading-tight" title={product.title}>
            {product.title}
          </Text>
          <div className="flex items-center justify-between gap-2">
            <div>
              {cheapestPrice ? (
                <Text className="text-sm font-semibold text-neutral-800">
                  {cheapestPrice.calculated_price}
                </Text>
              ) : (
                <Text className="text-xs text-neutral-500">Zahtevaj ponudbo</Text>
              )}
            </div>
            <Button
              variant="secondary"
              size="small"
              className="flex-shrink-0 h-8 w-8 p-0 rounded-full bg-[#6B8CAE] hover:bg-[#8BACD4] text-white border-0 shadow-sm"
              onClick={handleAddToCart}
              disabled={isAddingToCart}
              isLoading={isAddingToCart}
              title="Dodaj v košarico"
            >
              <span className="sr-only">Dodaj v košarico</span>
              <ShoppingBag className="w-4 h-4" fill="currentColor" />
            </Button>
          </div>
        </div>
      </div>
    </LocalizedClientLink>
  )
}
